// Shared UI primitives for the rework — thin React wrappers over the .u-*
// classes in ui.css (docs/design/branding-kit.html shows each one). Pages
// compose these; a page stylesheet only arranges them.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Check, ChevronLeft, ChevronRight, CircleAlert, CircleCheck, Ellipsis, Info, LoaderCircle, TriangleAlert, X,
} from 'lucide-react';

const cx = (...names) => names.filter(Boolean).join(' ');

export const PageHeader = ({ title, subtitle, actions, breadcrumb }) => (
  <header className="u-page-header">
    <div className="u-page-header__text">
      {breadcrumb && <nav className="u-breadcrumb" aria-label="breadcrumb">{breadcrumb}</nav>}
      <h1 className="u-page-title">{title}</h1>
      {subtitle && <p className="u-page-subtitle">{subtitle}</p>}
    </div>
    {actions && <div className="u-page-actions">{actions}</div>}
  </header>
);

export const IconChip = ({ icon: Icon, tone, size }) => (
  <span className={cx('u-icon-chip', tone && `u-icon-chip--${tone}`, size && `u-icon-chip--${size}`)} aria-hidden="true">
    <Icon strokeWidth={1.8} />
  </span>
);

export const Card = ({
  icon, iconTone, title, badge, description, actions, footer, footerNote,
  children, tone, flush, className, id,
}) => (
  <section className={cx('u-card', tone && `u-card--${tone}`, className)} id={id}>
    {(title || actions) && (
      <div className="u-card__header">
        {icon && <IconChip icon={icon} tone={iconTone} />}
        <div className="u-card__heading">
          {title && <h2 className="u-card__title">{title}{badge}</h2>}
          {description && <p className="u-card__desc">{description}</p>}
        </div>
        {actions && <div className="u-card__actions">{actions}</div>}
      </div>
    )}
    {children && <div className={cx('u-card__body', flush && 'u-card__body--flush')}>{children}</div>}
    {(footer || footerNote) && (
      <div className="u-card__footer">
        {footerNote && <span className="u-card__footer-note">{footerNote}</span>}
        {footer}
      </div>
    )}
  </section>
);

export const Badge = ({ tone, dot, icon: Icon, children, title }) => (
  <span className={cx('u-badge', tone && `u-badge--${tone}`)} title={title}>
    {dot && <span className="u-badge__dot" aria-hidden="true" />}
    {Icon && <Icon strokeWidth={2} aria-hidden="true" />}
    {children}
  </span>
);

export const Meter = ({ value, max = 100, tone, label }) => {
  const pct = max > 0 ? Math.max(0, Math.min(100, (value / max) * 100)) : 0;
  return (
    <span
      className={cx('u-meter', tone && `u-meter--${tone}`)}
      role="meter"
      aria-valuenow={value}
      aria-valuemin={0}
      aria-valuemax={max}
      aria-label={label}
    >
      <span style={{ width: `${pct}%` }} />
    </span>
  );
};

// label · value · context · optional meter. Clickable when onClick is given
// (a tile that leads to the place where the number is acted on).
export const Kpi = ({ icon, tone, label, value, unit, context, contextTone, meter, onClick, active, title }) => {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      className={cx('u-kpi', active && 'is-active')}
      onClick={onClick}
      title={title}
    >
      {icon && <IconChip icon={icon} tone={tone} />}
      <span className="u-kpi__body">
        <span className="u-overline">{label}</span>
        <span className="u-kpi__value">
          {value}
          {unit && <span className="u-kpi__unit">{unit}</span>}
        </span>
        {context && <span className={cx('u-kpi__context', contextTone && `u-kpi__context--${contextTone}`)}>{context}</span>}
        {meter && <Meter {...meter} />}
      </span>
    </Tag>
  );
};

export const Tabs = ({ tabs, active, onChange, label }) => (
  <div className="u-tabs" role="tablist" aria-label={label}>
    {tabs.map(({ key, label: tabLabel, icon: Icon, count }) => (
      <button
        key={key}
        type="button"
        role="tab"
        aria-selected={active === key}
        className={cx('u-tab', active === key && 'is-active')}
        onClick={() => onChange(key)}
      >
        {Icon && <Icon strokeWidth={1.8} aria-hidden="true" />}
        {tabLabel}
        {count != null && <span className="u-tab__count">{count}</span>}
      </button>
    ))}
  </div>
);

export const Segmented = ({ options, value, onChange, label }) => (
  <div className="u-seg" role="group" aria-label={label}>
    {options.map((option) => (
      <button
        key={option.value}
        type="button"
        className={cx('u-seg__btn', value === option.value && 'is-active')}
        aria-pressed={value === option.value}
        onClick={() => onChange(option.value)}
      >
        {option.label}
        {option.count != null && <span className="u-seg__count">{option.count}</span>}
      </button>
    ))}
  </div>
);

export const Field = ({ label, htmlFor, help, error, children }) => (
  <div className="u-field">
    {label && <label className="u-label" htmlFor={htmlFor}>{label}</label>}
    {children}
    {error ? <p className="u-error-text" role="alert">{error}</p> : help && <p className="u-help">{help}</p>}
  </div>
);

export const Switch = ({ checked, onChange, label, disabled, id }) => (
  <label className="u-switch" htmlFor={id}>
    <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
    <span className="u-switch__track" aria-hidden="true" />
    {label && <span>{label}</span>}
  </label>
);

const BANNER_ICONS = { info: Info, success: CircleCheck, warning: TriangleAlert, danger: CircleAlert, neutral: Info };

export const Banner = ({ tone = 'info', title, children, actions }) => {
  const Icon = BANNER_ICONS[tone] || Info;
  return (
    <div className={cx('u-banner', tone !== 'info' && `u-banner--${tone}`)} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon strokeWidth={2} aria-hidden="true" />
      <div className="u-banner__body">
        {title && <div className="u-banner__title">{title}</div>}
        {children}
      </div>
      {actions && <div className="u-banner__actions">{actions}</div>}
    </div>
  );
};

export const TOAST_DURATION_MS = 5000;

// The visible text of a node tree (strings, numbers, nested elements).
const textOf = (node) => {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (React.isValidElement(node)) return textOf(node.props.children);
  return '';
};

// The result of an action, top right (kit § Retours). It leaves by itself
// after 5 s — the count pauses while the pointer or the focus is on it and
// starts again when its message changes; an error (tone="danger") or a
// `persistent` toast stays until closed. onClose runs once per message,
// whoever closes it (timer or button). A page that also runs its own timer
// keeps working: whichever fires first removes the toast.
export const Toast = ({ tone = 'success', children, onClose, closeLabel = 'Fermer', persistent = false }) => {
  // Callers pass a new arrow each render: keep the latest in a ref so a
  // parent re-render never restarts (or doubles) the timer.
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; });
  const closedRef = useRef(false);
  const close = useCallback(() => {
    if (closedRef.current) return;
    closedRef.current = true;
    if (onCloseRef.current) onCloseRef.current();
  }, []);

  // A new message in the same toast (the page replaced it) is a new round:
  // it may close again and gets its full 5 s.
  const message = `${tone}|${textOf(children)}`;
  useEffect(() => { closedRef.current = false; }, [message]);

  const [paused, setPaused] = useState(false);
  const autoClose = !persistent && tone !== 'danger' && typeof onClose === 'function';
  useEffect(() => {
    if (!autoClose || paused) return undefined;
    const timer = setTimeout(close, TOAST_DURATION_MS);
    return () => clearTimeout(timer);
  }, [autoClose, paused, message, close]);

  return (
    <div className="u-toast-region">
      <div
        className={cx('u-toast', `u-toast--${tone}`)}
        role={tone === 'danger' ? 'alert' : 'status'}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocus={() => setPaused(true)}
        onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false); }}
      >
        {tone === 'danger' ? <CircleAlert strokeWidth={2} aria-hidden="true" /> : <CircleCheck strokeWidth={2} aria-hidden="true" />}
        <span className="u-toast__text">{children}</span>
        <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm" onClick={close} aria-label={closeLabel} title={closeLabel}>
          <X strokeWidth={2} />
        </button>
      </div>
    </div>
  );
};

// Activity in progress inside the control that started it (.u-spin).
export const Spinner = ({ label }) => (
  <LoaderCircle className="u-spin" strokeWidth={2} aria-hidden={label ? undefined : 'true'} aria-label={label} role={label ? 'img' : undefined} />
);

export const Modal = ({ title, description, icon, iconTone, onClose, children, footer, wide, closeLabel = 'Fermer' }) => {
  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="u-modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={cx('u-modal', wide && 'u-modal--wide')} role="dialog" aria-modal="true" aria-label={typeof title === 'string' ? title : undefined}>
        <div className="u-modal__header">
          {icon && <IconChip icon={icon} tone={iconTone} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="u-modal__title">{title}</h2>
            {description && <p className="u-modal__desc">{description}</p>}
          </div>
          <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm" onClick={onClose} aria-label={closeLabel} title={closeLabel}>
            <X strokeWidth={2} />
          </button>
        </div>
        {children && <div className="u-modal__body">{children}</div>}
        {footer && <div className="u-modal__footer">{footer}</div>}
      </div>
    </div>
  );
};

// Every destructive action passes through this: a title that names the
// object, the consequence in one sentence, the solid red button last.
export const ConfirmDialog = ({ title, message, confirmLabel, cancelLabel, onConfirm, onCancel, busy, icon }) => (
  <Modal
    title={title}
    description={message}
    icon={icon || TriangleAlert}
    iconTone="danger"
    onClose={onCancel}
    footer={(
      <>
        <button type="button" className="u-btn u-btn--secondary" onClick={onCancel} disabled={busy}>{cancelLabel}</button>
        <button type="button" className="u-btn u-btn--danger-solid" onClick={onConfirm} disabled={busy}>{confirmLabel}</button>
      </>
    )}
  />
);

// The « ⋯ » menu: secondary actions of a row, icon + text, danger last.
export const OverflowMenu = ({ items, label = 'Actions' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (event) => { if (ref.current && !ref.current.contains(event.target)) setOpen(false); };
    const onKey = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);
  const visible = items.filter(Boolean);
  if (!visible.length) return null;
  return (
    <div className="u-menu-anchor" ref={ref}>
      <button
        type="button"
        className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        onClick={() => setOpen((value) => !value)}
      >
        <Ellipsis strokeWidth={2} />
      </button>
      {open && (
        <div className="u-menu" role="menu">
          {visible.map(({ label: itemLabel, icon: Icon, onClick, danger, disabled, separator }, index) => (separator
            ? <div key={`sep-${index}`} className="u-menu__sep" />
            : (
              <button
                key={itemLabel}
                type="button"
                role="menuitem"
                className={cx('u-menu__item', danger && 'u-menu__item--danger')}
                disabled={disabled}
                onClick={() => { setOpen(false); onClick(); }}
              >
                {Icon && <Icon strokeWidth={1.8} aria-hidden="true" />}
                {itemLabel}
              </button>
            )))}
        </div>
      )}
    </div>
  );
};

export const EmptyState = ({ icon, title, children, action }) => (
  <div className="u-empty">
    {icon && <IconChip icon={icon} tone="neutral" />}
    {title && <div className="u-empty__title">{title}</div>}
    {children && <div>{children}</div>}
    {action}
  </div>
);

// Read-only facts: [{ label, value }], items without a value are skipped.
export const DescriptionList = ({ items }) => (
  <dl className="u-dl">
    {items.filter((item) => item && item.value !== undefined && item.value !== null && item.value !== '').map((item) => (
      <div key={item.label}>
        <dt>{item.label}</dt>
        <dd>{item.value}</dd>
      </div>
    ))}
  </dl>
);

// « 1–25 sur 312 » + previous / next. page is 1-based.
export const Pagination = ({ page, pageSize, total, onChange, summary, previousLabel = 'Page précédente', nextLabel = 'Page suivante' }) => {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const first = total ? (page - 1) * pageSize + 1 : 0;
  const last = Math.min(total, page * pageSize);
  return (
    <div className="u-pagination">
      <span>{summary ? summary({ first, last, total }) : `${first}–${last} / ${total}`}</span>
      <div className="u-pagination__pages">
        <button type="button" className="u-btn u-btn--secondary u-btn--icon u-btn--sm" onClick={() => onChange(page - 1)} disabled={page <= 1} aria-label={previousLabel} title={previousLabel}>
          <ChevronLeft strokeWidth={2} />
        </button>
        <span className="u-num">{page} / {pageCount}</span>
        <button type="button" className="u-btn u-btn--secondary u-btn--icon u-btn--sm" onClick={() => onChange(page + 1)} disabled={page >= pageCount} aria-label={nextLabel} title={nextLabel}>
          <ChevronRight strokeWidth={2} />
        </button>
      </div>
    </div>
  );
};

// History: [{ key, icon, tone, title, meta, time }].
export const Timeline = ({ items }) => (
  <ol className="u-timeline">
    {items.map(({ key, icon: Icon, tone, title, meta, time }) => (
      <li className="u-timeline__item" key={key}>
        <span className={cx('u-timeline__dot', tone && `u-timeline__dot--${tone}`)} aria-hidden="true">
          {Icon && <Icon strokeWidth={2} />}
        </span>
        <div>
          <div className="u-timeline__title">{title}</div>
          {meta && <div className="u-timeline__meta">{meta}</div>}
        </div>
        {time && <span className="u-timeline__time">{time}</span>}
      </li>
    ))}
  </ol>
);

// Side panel. By default over the page (a detail to read or edit): backdrop,
// Escape and a click outside close it. `docked`: beside the page, non-modal
// (an assistant, a detail kept open while working the list) — no backdrop,
// the page stays interactive and keeps the focus it has; Escape closes it
// only from inside the panel. Give the page .u-page--with-drawer so that,
// from 1280 px, its content makes room instead of sliding under the panel.
export const Drawer = ({ title, description, icon, onClose, children, footer, wide, docked = false, closeLabel = 'Fermer' }) => {
  const panelRef = useRef(null);
  useEffect(() => {
    const onKey = (event) => {
      if (event.key !== 'Escape') return;
      if (docked && !(panelRef.current && panelRef.current.contains(event.target))) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, docked]);
  useEffect(() => {
    if (!docked) return undefined;
    document.body.classList.add('u-drawer-docked-open');
    return () => document.body.classList.remove('u-drawer-docked-open');
  }, [docked]);
  const label = typeof title === 'string' ? title : undefined;
  return (
    <>
      {!docked && <div className="u-drawer-backdrop" onMouseDown={onClose} />}
      <aside
        ref={panelRef}
        className={cx('u-drawer', docked ? 'u-drawer--docked' : wide && 'u-drawer--wide')}
        role={docked ? 'complementary' : 'dialog'}
        aria-modal={docked ? undefined : 'true'}
        aria-label={label}
      >
        <div className="u-drawer__header">
          {icon && <IconChip icon={icon} />}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 className="u-modal__title">{title}</h2>
            {description && <p className="u-modal__desc">{description}</p>}
          </div>
          <button type="button" className="u-btn u-btn--ghost u-btn--icon u-btn--sm" onClick={onClose} aria-label={closeLabel} title={closeLabel}>
            <X strokeWidth={2} />
          </button>
        </div>
        <div className="u-drawer__body">{children}</div>
        {footer && <div className="u-drawer__footer">{footer}</div>}
      </aside>
    </>
  );
};

// Ordered setup: [{ key, title, description, status: 'done' | 'current' | 'todo', action }].
export const Steps = ({ steps }) => (
  <ol className="u-steps">
    {steps.map((step, index) => (
      <li key={step.key} className={cx('u-step', step.status === 'done' && 'is-done', step.status === 'current' && 'is-current')}>
        <span className="u-step__marker" aria-hidden="true">{step.status === 'done' ? <Check strokeWidth={2.4} /> : index + 1}</span>
        <div>
          <div className="u-step__title">{step.title}</div>
          {step.description && <div className="u-step__desc">{step.description}</div>}
          {step.action && <div style={{ marginTop: 10 }}>{step.action}</div>}
        </div>
      </li>
    ))}
  </ol>
);

export const initialsOf = (text) => {
  const words = String(text || '').replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean);
  if (!words.length) return 'U';
  return (words.length === 1 ? words[0].slice(0, 2) : `${words[0][0]}${words[1][0]}`).toUpperCase();
};
